import * as React from "react";
import { ArrowForward } from "@material-ui/icons";
import { GetCoreInfoRequestStatusEnum } from "../../../../Enums/CoreInfoRequestStatusEnum";
import { formatDate } from "../../../../Helper/MomentHelper";
import {
  CoreInfoRequestHistory,
  PbhRequestUnserviceableHistory,
  FormattedHistoryRow,
  FormattedPbhHistoryRow,
} from "../types/interfaces";
import { formatMinutesToHourMinute } from "../../../../Helper/FormatMinutesToHourMinute";

// Helper function to get status description from enum
export const getStatusDescription = (statusValue: number): string => {
  const statusItem = GetCoreInfoRequestStatusEnum.find(s => s.Value === statusValue);
  return statusItem?.Description || statusValue.toString();
};

// Generic function to format field changes with arrow indicator
export const formatFieldWithChange = (
  currentValue: any,
  previousValue: any,
  isDate = false,
  isMinutesField = false
): any => {
  // Handle null/undefined values
  const current = currentValue === null || currentValue === undefined ? null : currentValue;
  const previous = previousValue === null || previousValue === undefined ? null : previousValue;

  // If no previous value (first record), show current value or "Not Changed"
  if (previous === null) {
    if (current === null) return "Not Changed";

    // Format based on field type
    if (isMinutesField && current !== null) {
      return formatMinutesToHourMinute(current);
    }

    return isDate ? formatDate(current, true) : current;
  }

  // Format values based on field type
  let formattedCurrent = current;
  let formattedPrevious = previous;

  if (isMinutesField) {
    formattedCurrent = current !== null ? formatMinutesToHourMinute(current) : null;
    formattedPrevious = previous !== null ? formatMinutesToHourMinute(previous) : null;
  } else if (isDate) {
    formattedCurrent = current !== null ? formatDate(current, true) : null;
    formattedPrevious = previous !== null ? formatDate(previous, true) : null;
  }

  // Compare values
  if (formattedPrevious === formattedCurrent || previous === current) {
    return "Not Changed";
  }

  // Show change with arrow
  const prevDisplay = formattedPrevious === null ? "null" : formattedPrevious;
  const currDisplay = formattedCurrent === null ? "null" : formattedCurrent;

  return (
    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>{prevDisplay}</span>
      <ArrowForward style={{ fontSize: "16px", color: "#c80c0f" }} />
      <span>{currDisplay}</span>
    </span>
  );
};

// Format status field with enum description and change indicator
export const formatStatusField = (currentStatus: number, previousStatus: number | null | undefined): any => {
  const currentDesc = getStatusDescription(currentStatus);

  if (previousStatus === null || previousStatus === undefined) {
    return currentDesc;
  }

  if (previousStatus === currentStatus) {
    return "Not Changed";
  }

  const previousDesc = getStatusDescription(previousStatus);
  return (
    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>{previousDesc}</span>
      <ArrowForward style={{ fontSize: "16px", color: "#c80c0f" }} />
      <span>{currentDesc}</span>
    </span>
  );
};

// Format Core Info Request History Row
export const formatHistoryRow = (history: CoreInfoRequestHistory): FormattedHistoryRow => {
  const obj = history.objectData;
  const prevObj = history.previousObjectData;

  return {
    status: formatStatusField(obj.status, prevObj?.status),
    requestedFields: formatFieldWithChange(obj.requestedFields, prevObj?.requestedFields),
    remark: formatFieldWithChange(obj.remark, prevObj?.remark),
    accomplishDate: formatFieldWithChange(obj.accomplishDate, prevObj?.accomplishDate, true),
    respondedDate: formatFieldWithChange(obj.respondedDate, prevObj?.respondedDate, true),
    createdBy: obj.createdBy || "N/A",
    updatedBy: obj.updatedBy || "N/A",
    createdDate: formatDate(obj.createdDate, true),
    updatedDate: obj.updatedDate ? formatDate(obj.updatedDate, true) : "N/A",
  };
};

// Format PBH Unserviceable History Row
export const formatPbhHistoryRow = (history: PbhRequestUnserviceableHistory): FormattedPbhHistoryRow => {
  const obj = history.pbhRequestUnserviceableObjectData;
  const prevObj = history.previousPbhRequestUnserviceableObjectData;

  return {
    tso: formatFieldWithChange(obj.tso, prevObj?.tso, false, true),
    tsr: formatFieldWithChange(obj.tsr, prevObj?.tsr, false, true),
    tsn: formatFieldWithChange(obj.tsn, prevObj?.tsn, false, true),
    cso: formatFieldWithChange(obj.cso, prevObj?.cso),
    csr: formatFieldWithChange(obj.csr, prevObj?.csr),
    csn: formatFieldWithChange(obj.csn, prevObj?.csn),
    dsr: formatFieldWithChange(obj.dsr, prevObj?.dsr),
    dsn: formatFieldWithChange(obj.dsn, prevObj?.dsn),
    dom: formatFieldWithChange(obj.dom, prevObj?.dom),
    removalReasonDetail: formatFieldWithChange(obj.removalReasonDetail, prevObj?.removalReasonDetail),
    nha: formatFieldWithChange(obj.nha, prevObj?.nha),
    nla: formatFieldWithChange(obj.nla, prevObj?.nla),
    engineSerialNumber: formatFieldWithChange(obj.engineSerialNumber, prevObj?.engineSerialNumber),
    adSbMod: formatFieldWithChange(obj.adSbMod, prevObj?.adSbMod),
    createdBy: obj.createdBy || "N/A",
    updatedBy: obj.updatedBy || "N/A",
    createdDate: formatDate(obj.createdDate, true),
    updatedDate: obj.updatedDate ? formatDate(obj.updatedDate, true) : "N/A",
  };
};
